import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const LoginView: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTenantSelect, setShowTenantSelect] = useState(false);
  const { login, checkTenants, availableTenants, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Clear sensitive credentials from component state
  const clearCredentials = () => {
    setPassword('');
  };

  // Redirect to the home view when already authenticated (e.g., via iframe SSO)
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate]);

  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const tenants = await checkTenants(username, password);
      
      // Check if any tenant has auto_select flag (from hostname)
      const autoSelectTenant = tenants.find(t => t.auto_select);
      
      if (autoSelectTenant) {
        // Auto-login with tenant from hostname
        await login(username, password, autoSelectTenant.id);
        clearCredentials();
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from);
      } else if (tenants.length === 1) {
        // If only one tenant, login directly
        await login(username, password, tenants[0].id);
        clearCredentials();
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from);
      } else if (tenants.length > 1) {
        // Show tenant selection if multiple tenants
        setShowTenantSelect(true);
      } else {
        throw new Error('Invalid username or password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password, selectedTenant);
      clearCredentials();
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setShowTenantSelect(false);
    setUsername('');
    setPassword('');
    setSelectedTenant('');
    setError('');
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-box">
          <h2>Login</h2>
          {error && <div className="error-message">{error}</div>}
          
          {!showTenantSelect ? (
            <form onSubmit={handleInitialSubmit}>
              <div className="form-group">
                <label htmlFor="username">Email</label>
                <input
                  type="email"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleTenantSubmit}>
              <div className="form-group">
                <label htmlFor="tenant">Select Tenant</label>
                <select
                  id="tenant"
                  value={selectedTenant}
                  onChange={async (e) => {
                    const value = e.target.value;
                    if (value) {
                      setSelectedTenant(value);
                      setIsLoading(true);
                      try {
                        await login(username, password, value);
                        clearCredentials();
                        const from = (location.state as any)?.from?.pathname || '/';
                        navigate(from);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Login failed');
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }}
                  required
                  className="tenant-select"
                  disabled={isLoading}
                >
                  <option value="">Select a tenant...</option>
                  {availableTenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.id}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="secondary"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </button>
            </form>
          )}
          
          <div className="auth-links">
            <a href="/register">New User? Register Here</a>
            <br /><br />
            <a href="/add-tenant">Already have an account? Add Tenant</a>
            <br /><br />
            {/* <a href="/forgot-password">Forgot password?</a> */}
          </div>
        </div>
      </div>
    </div>
  );
}; 
